'use client';

import React, { useState, useRef, JSX, DragEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';

import { Text, Button } from '@videoblocks/design-system';
import { UploadBottom } from '@videoblocks/react-icons';

import { validateFileSize } from './utils';

const UploadInput = ({
    handleFileUpload,
    isPending,
}: {
    handleFileUpload: (file: File) => void;
    isPending?: boolean;
}): JSX.Element => {
    const [inputError, setInputError] = useState<string | undefined>(undefined);

    const [isDragActive, setIsDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setIsDragActive(false);

        const dt = event.dataTransfer;
        if (dt && dt.files && dt.files.length > 0) {
            const file = dt.files[0];
            if (!validateFileSize(file, setInputError)) {
                return;
            }
            handleFileUpload(file);
        }
    };

    const handleChooseFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!validateFileSize(file, setInputError)) {
                return;
            }
            handleFileUpload(file);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDragEnter: DragEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave: DragEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDragOver: DragEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <>
            <div
                data-testid="upload-input-container"
                className={twMerge(
                    'relative rounded-lg flex flex-col items-center justify-center bg-gray-900 transition-colors duration-200 border-2 border-gray-900 h-60',
                    isDragActive && 'border-2 border-white border-dashed',
                    !!inputError && 'border-2 border-red-400',
                )}
            >
                <div className={`absolute ${isPending ? 'block' : 'hidden'}`}>
                    <div className="flex items-center flex-col justify-center gap-3">
                        <Text fontWeight="regular">Uploading your file...</Text>
                        <svg
                            className="animate-spin"
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M4 24C4 35.0457 12.9543 44 24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4"
                                stroke="white"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </div>
                <div
                    className={`py-10 flex flex-col justify-center items-center gap-3 w-full  ${isPending ? 'invisible' : 'visible'}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                >
                    <UploadBottom className="w-6 h-6 fill-current" />
                    <Text fontSize="lg">
                        {isDragActive ? 'Drop your files here' : 'Click to upload or drag and drop your recording'}
                    </Text>
                    <Text fontWeight="regular">Works with audio or video files, up to 10MB each</Text>
                    <Button onClick={handleChooseFile}>Choose File</Button>
                    <input
                        data-testid="upload-input"
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                    />
                </div>
            </div>
            {!!inputError && (
                <Text fontWeight="regular" className="text-red-100 text-left mt-2">
                    {inputError}
                </Text>
            )}
        </>
    );
};

export default UploadInput;
